import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import asyncAction from 'modules/core/lib/asyncAction'

import QuestionCategoryComponent from 'modules/reviews/components/QuestionCategories/Show/QuestionCategoryComponent'
import { ScoreTypes } from 'modules/reviews/lib/enums'
import { useReviewShowContext } from 'modules/reviews/pages/performance/Show/hooks'
import questionConfigs from 'modules/reviews/store/collections/questionConfigs'
import type QuestionConfig from 'modules/reviews/store/models/QuestionConfig'
import type QuestionGroupConfig from 'modules/reviews/store/models/QuestionGroupConfig'
import type Review from 'modules/reviews/store/models/v2/Review'

import QuestionConfigsList from './List'
import NoQuestionsAddedBlankSlate from './NoQuestionsAddedBlankSlate'
import ScoreQuestions from './ScoreType/ScoreQuestions'

type QuestionConfigsListProps = React.ComponentProps<typeof QuestionConfigsList>

interface Props {
  questionGroupConfig: QuestionGroupConfig
  form: Review
  hasPerformanceScoreQuestions: boolean
}

const QuestionConfigs = ({ questionGroupConfig, form, hasPerformanceScoreQuestions }: Props) => {
  const { updateCanBeStarted } = useReviewShowContext()
  const fetchQuestionConfigs = () =>
    questionConfigs.fetch({
      data: {
        surveys_form_id: form?.id,
      },
    })

  if (!form) return null

  const filteredScoreQuestions = questionConfigs.filter(
    (config: QuestionConfig) =>
      config.attributes.question_type !== null &&
      Object.values(ScoreTypes).includes(config.attributes.question_type) &&
      config.attributes.surveys_question_group_config_id == questionGroupConfig.attributes.id
  )

  const visibleQuestionConfigsWithoutCategory = questionGroupConfig.questionConfigsWithoutCategory
    .filter((questionConfig) => !questionConfig.attributes.archived)
    .filter((questionConfig) => !questionConfig.attributes.question_type)

  const archivedQuestionConfigsWithoutCategory = questionGroupConfig.questionConfigsWithoutCategory
    .filter((questionConfig) => questionConfig.attributes.archived)
    .filter((questionConfig) => !questionConfig.attributes.question_type)

  const questionCategories = questionGroupConfig.questionCategories

  const updateQuestionConfig: QuestionConfigsListProps['updateQuestionConfig'] = async (data) => {
    const params = {
      label: data.custom_field_params.label,
      required: data.custom_field_params.required,
      choice_options: data.custom_field_params.choice_options,
      id: data.question_config_id,
      surveys_form_id: form?.id,
    }
    const questionConfig = questionConfigs.find({
      id: data.question_config_id,
    })

    if (!questionConfig) {
      return
    }

    const { success, value } = await asyncAction(() => questionConfig.save(params))

    // We reorder all question configs in backend
    if (success) await fetchQuestionConfigs()

    return value
  }

  const createQuestionConfigFromDuplicate: QuestionConfigsListProps['createQuestionConfigFromDuplicate'] =
    async (data, questionConfigCategoryId) => {
      if (!questionGroupConfig) return

      const { success } = await asyncAction(() =>
        questionConfigs.create({
          question_group_config_id: questionGroupConfig.attributes.id,
          question_category_id: questionConfigCategoryId,
          surveys_form_id: form?.id,
          custom_field: data,
        })
      )

      if (success) await fetchQuestionConfigs()
    }

  const toggleArchivedQuestionConfig: QuestionConfigsListProps['toggleArchivedQuestionConfig'] =
    async (data) => {
      const questionConfig = questionConfigs.find({
        id: data.id,
      })

      if (!questionConfig) {
        return
      }

      const { success, value } = await asyncAction(() => questionConfig.rpc('toggle_archive', data))

      // We reorder all question configs in backend
      if (success) {
        await fetchQuestionConfigs()
        await updateCanBeStarted()
      }

      return value
    }

  const deleteQuestionConfig: QuestionConfigsListProps['deleteQuestionConfig'] = async (data) => {
    const questionConfig = questionConfigs.find({
      id: data.id,
    })

    if (!questionConfig) {
      return
    }

    const { success, value } = await asyncAction(() =>
      questionConfig.destroy({ data: { surveys_form_id: form?.id } })
    )

    // We reorder all question configs in backend
    if (success) {
      await fetchQuestionConfigs()
      await updateCanBeStarted()
    }

    return value
  }

  const reorderQuestionConfig: QuestionConfigsListProps['reorderQuestionConfig'] = async (data) => {
    const questionConfig = questionConfigs.find({
      id: data.id,
    })

    if (!questionConfig) {
      return
    }

    const { success, value } = await asyncAction(() =>
      questionConfig.rpc('reorder', {
        new_position: data.newPosition,
        surveys_form_id: form?.id,
      })
    )

    // We reorder all question configs in backend
    if (success) await fetchQuestionConfigs()

    return value
  }

  const isScore = false // TODO: performance score questions will not be editable - replace once BE part ready

  const hasAnyActiveQuestionsWithoutSections = Boolean(visibleQuestionConfigsWithoutCategory.length)
  const hasAnyArchivedQuestionsWithoutSections = Boolean(
    archivedQuestionConfigsWithoutCategory.length
  )
  const hasAnySections = Boolean(questionCategories.length)
  const hasNoQuestionsAndSectionsAdded =
    !hasAnyActiveQuestionsWithoutSections && !hasAnySections && !hasPerformanceScoreQuestions

  const isSelfOrManagerSection =
    questionGroupConfig.respondent?.attributes.name === 'self' ||
    questionGroupConfig.respondent?.attributes.name === 'manager'

  return (
    <>
      {hasNoQuestionsAndSectionsAdded && <NoQuestionsAddedBlankSlate />}

      {hasAnyActiveQuestionsWithoutSections && (
        <QuestionConfigsList
          form={form}
          questionConfigs={visibleQuestionConfigsWithoutCategory}
          deleteQuestionConfig={deleteQuestionConfig}
          reorderQuestionConfig={reorderQuestionConfig}
          createQuestionConfigFromDuplicate={createQuestionConfigFromDuplicate}
          toggleArchivedQuestionConfig={toggleArchivedQuestionConfig}
          updateQuestionConfig={updateQuestionConfig}
          isScore={isScore}
        />
      )}
      {hasAnyArchivedQuestionsWithoutSections && (
        <QuestionConfigsList
          archived
          form={form}
          questionConfigs={archivedQuestionConfigsWithoutCategory}
          deleteQuestionConfig={deleteQuestionConfig}
          reorderQuestionConfig={reorderQuestionConfig}
          createQuestionConfigFromDuplicate={createQuestionConfigFromDuplicate}
          toggleArchivedQuestionConfig={toggleArchivedQuestionConfig}
          updateQuestionConfig={updateQuestionConfig}
          isScore={isScore}
        />
      )}
      {hasAnySections &&
        questionCategories.map((questionCategory) => (
          <QuestionCategoryComponent
            key={`questionCategory-${questionCategory.id}`}
            form={form}
            questionGroupConfig={questionGroupConfig}
            questionCategory={questionCategory}
            deleteQuestionConfig={deleteQuestionConfig}
            reorderQuestionConfig={reorderQuestionConfig}
            createQuestionConfigFromDuplicate={createQuestionConfigFromDuplicate}
            toggleArchivedQuestionConfig={toggleArchivedQuestionConfig}
            updateQuestionConfig={updateQuestionConfig}
            isScore={isScore}
          />
        ))}
      {hasPerformanceScoreQuestions && isSelfOrManagerSection && (
        <Box>
          <ScoreQuestions filteredScoreQuestions={filteredScoreQuestions} />
        </Box>
      )}
    </>
  )
}

export default observer(QuestionConfigs)
